import { Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { roundToTwo } from '../../../MACRA_1/components/InputForm/Geometry/utils';
import { IVerticalDropsResults } from '../../../MACRA_2/api/IApiResponse';
import { EVerticalDropsTypes } from '../../../MACRA_2/enum/general';
import { IProjectMacra2 } from '../../../MACRA_2/store/project/projectSlice';
import { conversionLabel } from '../../../conversion';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { getVerticalDropsType } from '../../../utils/utils';
import { PdfCrossCheck, styles } from '../PDFStyle';
import PDFTable from '../PDFTable/PDFTable';

interface IPressureResultsProps {
    project: IProjectMacra2;
    projectInfo: IProjectInfoForm;
    results: IVerticalDropsResults;
}

const BasinDesignResults = (props: IPressureResultsProps) => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;
    const type: EVerticalDropsTypes = getVerticalDropsType(project.stillingBasin!);

    const dissipationBasin: string[][] = [
        [
            t('output_hydraulic_vertical_location_hydraulic_jump_start'),
            'Lg1',
            `${roundToTwo(results.hydraulicResult.locationOfHydraulicJumpStart)}`,
            conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
            '-',
        ],
        [
            t('output_hydraulic_vertical_min_basin_length'),
            '@L(bas_min)',
            `${roundToTwo(results.hydraulicResult.minimumBasinLengthUsace1990)}`,
            conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
            project.stillingBasin?.poolLength! >= results.hydraulicResult.minimumBasinLengthUsace1990! ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
    ];
    const scourLocation: string[][] = [
        [t('output_hydraulic_vertical_scour_location'), 'X', `${roundToTwo(results.hydraulicResult.scourLocation)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number], '-'],
    ];

    const materialAndStress: string[][] = [
        [
            t('output_hydraulic_vertical_stepped_sloped_lining_material'),
            `${project.stillingBasin?.material.name!}`,
            `t=${roundToTwo(project.stillingBasin?.material.physicalData![0].thickness)} ${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            `d50=${roundToTwo(project.stillingBasin?.material.physicalData![0].rockD50)} ${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            `Cu=${roundToTwo(project.stillingBasin?.material.physicalData![0].cu)}`,
        ],
        [t('output_hydraulic_sloped_basin_lining_thickness'), '@t(b)', `${roundToTwo(project.stillingBasin?.thickness)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`, '-'],
        [
            t('output_hydraulic_vertical_stepped_sloped_shear_stress_downstream_section'),
            '@τ(bas_calc)',
            `${roundToTwo(results.hydraulicResult.shearStressInTheBasin)}`,
            conversionLabel.Nm2_lbfft2[projectInfo.unitOfMeasure as number],
            results.hydraulicResult.shearStressInTheBasin! < project.stillingBasin?.material.physicalData![0].allowShearStress! ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [
            t('output_hydraulic_vertical_stepped_sloped_allowable_shear_stress_lining'),
            `@τ(all)`,
            `${roundToTwo(project.stillingBasin?.material.physicalData![0].allowShearStress)}`,
            `${conversionLabel.Nm2_lbfft2[projectInfo.unitOfMeasure as number]}`,
            '-',
        ],
    ];

    let scour: string[][] = [
        [t('output_hydraulic_vertical_scour_location'), 'X', `${roundToTwo(results.hydraulicResult.scourLocation)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number], '-'],
    ];

    switch (type) {
        case EVerticalDropsTypes.UnlinedVerticalDrop:
            scour = [
                ...scourLocation,
                [t('output_hydraulic_vertical_scour_depth'), '@f(b)', `${roundToTwo(results.hydraulicResult.scourDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number], '-'],
            ];
            break;
        case EVerticalDropsTypes.UnlinedPoolAndCountweir:
            scour = [
                [t('output_hydraulic_vertical_scour_depth'), '@f(b)', `${roundToTwo(results.hydraulicResult.scourDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number], '-'],
                ...scourLocation,
                ...dissipationBasin,
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
            scour = [
                [t('output_hydraulic_vertical_scour_depth'), '@f(b)', `${roundToTwo(results.hydraulicResult.scourDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number], '-'],
                ...scourLocation,
                ...dissipationBasin,
                ...materialAndStress,
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
            scour = [...scourLocation, ...dissipationBasin, ...materialAndStress];
            break;

        default:
            break;
    }
    return (
        <View>
            <View style={styles.rowSpaced}>
                <Text style={styles.boldGreen}>{t('graphical_results_basinDesign')}</Text>
            </View>
            <PDFTable columns={[t('output_static_vertical_stepped_basin_design').toUpperCase(), t('output_symbol'), t('output_value'), t('output_unit'), t('output_check')]} data={scour} />
        </View>
    );
};

export default BasinDesignResults;
